import React, { useState } from 'react';
import { motion } from 'motion/react';
import { Helmet } from 'react-helmet-async';
import { useUser } from '../context/UserContext';
import { useCart } from '../context/CartContext';
import { useBrand, MembershipPlan } from '../context/BrandContext';
import { Check, Shield, Zap, Star, ArrowRight, Compass, Crown, Gem } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function Membership() {
  const { user, profile, loading: authLoading, signIn } = useUser();
  const { membershipPlans: brandPlans, assets } = useBrand();
  const { addToCart } = useCart();
  const navigate = useNavigate();
  const plans = brandPlans || [];
  const [processing, setProcessing] = useState<string | null>(null);

  const getIcon = (iconName: string) => {
    switch (iconName) {
      case 'Compass': return <Compass className="h-12 w-12 text-ows-teal mb-6" />;
      case 'Zap': return <Zap className="h-12 w-12 text-ows-teal mb-6" />;
      case 'Crown': return <Crown className="h-12 w-12 text-ows-teal mb-6" />;
      case 'Gem': return <Gem className="h-12 w-12 text-ows-teal mb-6" />;
      case 'Star': return <Star className="h-12 w-12 text-ows-teal mb-6" />;
      default: return <Shield className="h-12 w-12 text-ows-teal mb-6" />;
    }
  };

  const handleSelectPlan = async (plan: MembershipPlan) => {
    if (!user) {
      navigate(`/login?redirect=/membership-checkout?planId=${plan.id}`);
      return;
    }

    setProcessing(plan.id);
    // Simulate a brief delay for a premium feel
    await new Promise(resolve => setTimeout(resolve, 800));
    
    setProcessing(null);
    navigate(`/membership-checkout?planId=${plan.id}`);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center bg-ows-white">
        <div className="text-ows-teal animate-pulse tracking-widest uppercase text-xs">Initializing Syndicate Access...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 pb-20 bg-ows-white overflow-hidden relative">
      <Helmet>
        <title>Syndicate Membership | Join the OWS Inner Circle</title>
        <meta name="description" content="Join the OWS Syndicate for exclusive access to advanced telemetry, priority builds, and our global expedition network. Choose your membership tier today." />
        <meta name="keywords" content="OWS membership, adventure club, expedition support, maritime syndicate, premium water gear" />
        <link rel="canonical" href="https://ais-pre-simrsubn4sndvaezn43il3-146117473355.us-east1.run.app/membership" />
      </Helmet>
      <div className="absolute top-0 left-0 w-full h-full pointer-events-none opacity-[0.03]">
        <div className="absolute top-1/4 -left-20 w-96 h-96 border border-ows-teal rounded-full animate-pulse" />
        <div className="absolute bottom-1/4 -right-20 w-96 h-96 border border-ows-teal rounded-full animate-pulse delay-1000" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h1 className="ows-header mb-8">The Syndicate</h1>
            <p className="max-w-2xl mx-auto text-sm tracking-widest uppercase text-ows-dark/60 leading-relaxed">
              Exclusive access to the inner circle of OWS. Advanced telemetry, priority builds, and the global expedition network.
            </p>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className={`relative p-10 border ${profile?.membershipTier === plan.name ? 'border-ows-teal bg-ows-teal/5' : 'border-ows-teal/10 bg-ows-sand/10'} hover:border-ows-teal/30 transition-all group`}
            >
              {profile?.membershipTier === plan.name && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-ows-teal text-white px-4 py-1 text-[10px] tracking-widest uppercase">
                  Current Plan
                </div>
              )}
              
              <div className="mb-8">
                {getIcon(plan.icon)}
                <h3 className="text-2xl font-light tracking-[0.2em] uppercase mb-4">{plan.name}</h3>
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-light">${plan.price}</span>
                  <span className="text-[10px] tracking-widest uppercase text-ows-dark/40">/ per {plan.interval || 'month'}</span>
                </div>
              </div>

              <ul className="space-y-4 mb-12">
                {plan.features.map((feature, i) => (
                  <li key={`${plan.id}-feature-${feature}-${i}`} className="flex items-start gap-3 text-xs tracking-widest uppercase text-ows-dark/60">
                    <Check className="h-4 w-4 text-ows-teal shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleSelectPlan(plan)}
                disabled={processing !== null || profile?.membershipTier === plan.name}
                className={`w-full py-4 text-[10px] tracking-[0.3em] uppercase transition-all flex items-center justify-center gap-2 ${
                  profile?.membershipTier === plan.name
                    ? 'bg-ows-dark/10 text-ows-dark/40 cursor-default'
                    : 'bg-ows-teal text-white hover:bg-ows-teal/90'
                }`}
              >
                {processing === plan.id ? (
                  'Processing...'
                ) : profile?.membershipTier === plan.name ? (
                  'Active'
                ) : (
                  <>
                    Select Tier <ArrowRight className="h-3 w-3" />
                  </>
                )}
              </button>
            </motion.div>
          ))}
        </div>

        <div className="mt-24 grid grid-cols-1 md:grid-cols-3 gap-12 border-t border-ows-teal/10 pt-20">
          <div className="text-center">
            <Shield className="h-8 w-8 mx-auto mb-6 text-ows-teal/40" />
            <h4 className="text-xs tracking-[0.2em] uppercase mb-4">Secure Access</h4>
            <p className="text-[10px] tracking-widest uppercase text-ows-dark/40 leading-relaxed">
              Encrypted telemetry and private communication channels for all Syndicate members.
            </p>
          </div>
          <div className="text-center">
            <Zap className="h-8 w-8 mx-auto mb-6 text-ows-teal/40" />
            <h4 className="text-xs tracking-[0.2em] uppercase mb-4">Priority Support</h4>
            <p className="text-[10px] tracking-widest uppercase text-ows-dark/40 leading-relaxed">
              Direct line to the engineering team for custom vessel modifications and technical advice.
            </p>
          </div>
          <div className="text-center">
            <Star className="h-8 w-8 mx-auto mb-6 text-ows-teal/40" />
            <h4 className="text-xs tracking-[0.2em] uppercase mb-4">Global Network</h4>
            <p className="text-[10px] tracking-widest uppercase text-ows-dark/40 leading-relaxed">
              Access to OWS supply caches and secure harbors across the Baja peninsula.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
